import { useMutation } from "@tanstack/react-query";
import { Skeleton } from "antd";
import { FacilityOnboardingStep, updateFacilityOnboardingSteps } from "src/api/facility";
import { HCF_USER_EVENTS } from "src/constants/analyticsEvents";
import { VerticalSpacing } from "src/designsystem/VerticalSpacing/VerticalSpacing";
import { SessionType } from "src/modules/interface";
import { UPDATE_PROFILE } from "src/modules/session";
import { APP_V2_USER_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useRef, useState, useEffect } from "react";
import { HiOutlineArrowLeft, HiOutlineArrowRight } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  ButtonIcon,
  Description,
  Heading,
  LinkButtonWithIcon,
  PageTitle,
  StyledButton,
  Tasks,
  WhatElse,
  Wrapper,
} from "./FacilityOnboarding.styles";
import { Carousel } from "./components/Carousel";
import { OnboardingStep } from "./components/OnboardingStep";
import { PostShiftOptionsModal } from "./components/PostShiftOptionsModal";
import { useOnboardingSteps } from "./useOnboardingSteps";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { useWorkerBlockBookingFeatureFlag } from "src/containers/workplaceBlockShifts/useWorkerBlockBookingFeatureFlag";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { CarouselRef } from "antd/lib/carousel";
import { WorkerInvitationOnboardingStep } from "./components/WorkerInvitationOnboardingStep";
import { WorkerInvitationDialog } from "@src/appV2/WorkerInvitation/WorkerInvitationDialog";
import { useModalState } from "@src/lib/ui-react";
import { showErrorToast } from "@src/appV2/lib/Notifications";

export const GETTING_STARTED = {
  FIND_MORE_WORKERS: "find more workers",
  POST_MORE_SHIFTS: "post more shifts",
  BOOK_QUALIFIED_WORKER: "book qualified worker",
  CAROUSEL_PREVIOUS_SLIDE: "carousel previous slide",
  CAROUSEL_NEXT_SLIDE: "carousel next slide",
  DOWNLOAD_MOBILE_APP: "download mobile app",
  ADD_TEAM_MEMBER: "add team member",
  ADD_CHECK_IN_INSTRUCTIONS: "add check-in instructions",
  TAKE_PRODUCT_TOUR: "take product tour",
  TEAM_ADDITION_INFO_POPOVER_CLOSED: "team addition info popover closed",
} as const;
type GettingStartedActionKeys = keyof typeof GETTING_STARTED;
export type GettingStartedActionValues = (typeof GETTING_STARTED)[GettingStartedActionKeys];

export function FacilityOnboarding() {
  const dispatch = useDispatch();
  const { profile, user, admin } = useSelector((state: SessionType) => state.session);
  const [showPostShiftOptionsModal, setShowPostShiftOptionsModal] = useState(false);
  const postShiftOptionSteps = useCbhFlag(CbhFeatureFlag.POST_SHIFT_OPTIONS_AB_TESTING, {
    defaultValue: {
      orders: ["block-shifts", "per-diem"],
    },
  }).orders;
  const hcfOnboarding = useCbhFlag(CbhFeatureFlag.HCF_ONBOARDING, {
    defaultValue: {},
  });
  const isWorkerBlockBookingEnabled = useWorkerBlockBookingFeatureFlag();
  const carouselRef = useRef<CarouselRef>(null);
  const history = useHistory();

  const {
    data: onboardingSteps,
    isLoading: onboardingStepsLoading,
    invalidateCache: invalidateOnboardingSteps,
  } = useOnboardingSteps(profile.userId, user?._id);

  // prefer object destructuring --> const { mutate: updateOnboardingSteps  } = useMutation`
  // over whole object --> `const updateOnboardingStepsMutation = useMutation`
  // because updateOnboardingStepsMutation's identify is changed for each render
  // while updateOnboardingSteps's identify is the same
  const { mutateAsync: updateOnboardingSteps, isLoading: isUpdatingOnboardingSteps } = useMutation({
    mutationFn: ({
      workplaceId,
      steps,
      workplaceUserId,
    }: {
      workplaceId: string;
      steps: Partial<FacilityOnboardingStep>;
      workplaceUserId: string;
    }) => updateFacilityOnboardingSteps(workplaceId, steps, workplaceUserId),
    onError: (error) => {
      showErrorToast("Failed to skip. Please try again.");
      logError(APP_V2_USER_EVENTS.FAILED_TO_SKIP_WORKER_INVITATION_ONBOARDING_STEP, {
        error,
      });
    },
  });

  const workerInvitationModalState = useModalState();
  const { isEnabled: isWorkplaceToWorkerReferralsEnabled } = useCbhFlag(
    CbhFeatureFlag.WORKPLACE_TO_WORKER_REFERRALS,
    {
      defaultValue: {
        isEnabled: false,
      },
    }
  );

  useEffect(() => {
    async function syncOnboarding() {
      if (!profile || !onboardingSteps || !hcfOnboarding) {
        return;
      }

      // Kick-off workplace onboarding progress if
      // - workplace hasn't started yet
      // - not complete any tasks (post shifts) yet
      // - workplace's createdAt is after LDD minimum creation date
      const { minimumWorkplaceCreationDate } = hcfOnboarding;
      const workplaceInExperiment =
        minimumWorkplaceCreationDate && profile.createdAt
          ? new Date(profile.createdAt) > new Date(minimumWorkplaceCreationDate)
          : false;

      if (user?._id && onboardingSteps.stage === "pending" && workplaceInExperiment) {
        const resp = await updateOnboardingSteps({
          workplaceId: profile.userId,
          steps: {
            stage: "started",
          },
          workplaceUserId: user._id,
        });
        dispatch({
          type: UPDATE_PROFILE,
          data: { ...profile, onboardingSteps: resp },
        });
        invalidateOnboardingSteps();
      }
    }

    void syncOnboarding();
  }, [
    dispatch,
    hcfOnboarding,
    invalidateOnboardingSteps,
    onboardingSteps,
    profile,
    updateOnboardingSteps,
    user?._id,
  ]);

  useEffect(() => {
    if (onboardingSteps) {
      dispatch({
        type: UPDATE_PROFILE,
        data: { ...profile, onboardingSteps },
      });
    }
    // profile is removed from dependency array because it's not used in the effect, and adding it would cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onboardingSteps]);

  const logGettingStartedEvents = (action: GettingStartedActionValues, subAction?: string) => {
    if (admin) {
      return;
    }

    const commonAttributes = {
      postShiftOptionSteps,
      workplaceId: profile.userId,
      workplaceUserId: user?._id,
      onboardingState: onboardingSteps,
    };

    logEvent(HCF_USER_EVENTS.GETTING_STARTED, {
      ...commonAttributes,
      action,
      subAction,
    });
  };

  useEffect(() => {
    function logPageView() {
      if (!profile.userId || !onboardingSteps || admin) {
        return;
      }
      logEvent(HCF_USER_EVENTS.GETTING_STARTED_PAGE_VIEWED, {
        postShiftOptionSteps,
        workplaceId: profile.userId,
        workplaceUserId: user?._id,
        onboardingState: onboardingSteps,
      });
    }
    logPageView();
  }, [admin, onboardingSteps, postShiftOptionSteps, profile.userId, user?._id]);

  const renderShiftsStep = (visibleStepCount: number) => {
    const handleClick = (action: GettingStartedActionValues) => {
      if (isWorkerBlockBookingEnabled) {
        setShowPostShiftOptionsModal(true);
      } else {
        // No need of opening block booking modal, if the feature is disabled. Instead redirect the user to calendar page.
        history.push(getLocation("facilityDashboard"));
      }
      logGettingStartedEvents(action);
    };
    return onboardingSteps?.postShifts ? (
      <OnboardingStep
        key="post-shifts"
        title={<>You posted your first staffing request!</>}
        description={
          <LinkButtonWithIcon
            underline={false}
            color="primary"
            onClick={() => handleClick(GETTING_STARTED.POST_MORE_SHIFTS)}
          >
            Post more shifts
            <HiOutlineArrowRight />
          </LinkButtonWithIcon>
        }
        completed
      />
    ) : (
      <OnboardingStep
        key="post-shifts"
        title={<>Post your staffing needs</>}
        description={
          <>
            Post individual shifts or a block of shifts. Whatever your needs are, you'll get access
            to qualified professionals who can meet them.
          </>
        }
        step={visibleStepCount}
        completed={false}
        footer={
          <StyledButton
            variant={visibleStepCount === 1 ? "primary" : "secondary"}
            size="lg"
            onClick={() => handleClick(GETTING_STARTED.BOOK_QUALIFIED_WORKER)}
          >
            Post your first shift
          </StyledButton>
        }
      />
    );
  };

  const handleCarouselPreviousClick = () => {
    logGettingStartedEvents(GETTING_STARTED.CAROUSEL_PREVIOUS_SLIDE);
    carouselRef.current?.prev();
  };
  const handleCarouselNextClick = () => {
    logGettingStartedEvents(GETTING_STARTED.CAROUSEL_NEXT_SLIDE);
    carouselRef.current?.next();
  };

  return (
    <Wrapper>
      <PageTitle>Fill your shifts with trusted professionals</PageTitle>
      <Description>Here's how to get started...</Description>
      <VerticalSpacing size="xl" />
      {onboardingStepsLoading ? (
        <>
          <Skeleton avatar paragraph={{ rows: 1 }} />
          <VerticalSpacing size="xl" />
          <Skeleton avatar paragraph={{ rows: 1 }} />
        </>
      ) : (
        <>
          {isWorkplaceToWorkerReferralsEnabled && (
            <WorkerInvitationOnboardingStep
              isLoading={isUpdatingOnboardingSteps || onboardingStepsLoading}
              visibleStepCount={1}
              isCompleted={Boolean(onboardingSteps?.hasSentWorkerInvitations)}
              onSkip={async () => {
                await updateOnboardingSteps({
                  workplaceId: profile.userId,
                  steps: { hasSentWorkerInvitations: true },
                  workplaceUserId: user!._id,
                });
                logEvent(APP_V2_USER_EVENTS.SKIPPED_WORKER_INVITATION_ONBOARDING_STEP, {
                  workplaceId: profile.userId,
                });
                invalidateOnboardingSteps();
              }}
              onInvite={() => {
                workerInvitationModalState.openModal();
              }}
            />
          )}
          {renderShiftsStep(isWorkplaceToWorkerReferralsEnabled ? 2 : 1)}
          <WhatElse>
            <Heading>See what else you can do</Heading>
            <Tasks>
              <ButtonIcon
                variant="secondary"
                onClick={handleCarouselPreviousClick}
                justifySelf="right"
                aria-label="previous carousel"
              >
                <HiOutlineArrowLeft />
              </ButtonIcon>
              <Carousel
                ref={carouselRef}
                onboardingSteps={onboardingSteps}
                logGettingStartedEvents={logGettingStartedEvents}
                isAdmin={admin}
              />
              <ButtonIcon
                variant="secondary"
                onClick={handleCarouselNextClick}
                justifySelf="left"
                aria-label="next carousel"
              >
                <HiOutlineArrowRight />
              </ButtonIcon>
            </Tasks>
          </WhatElse>
        </>
      )}
      {showPostShiftOptionsModal && onboardingSteps && (
        <PostShiftOptionsModal
          visible={showPostShiftOptionsModal}
          onCancel={() => setShowPostShiftOptionsModal(false)}
          postShiftOptionSteps={postShiftOptionSteps}
          workplaceId={profile.userId}
          workplaceUserId={user?._id}
          onboardingState={onboardingSteps}
          isAdmin={admin}
        />
      )}
      <WorkerInvitationDialog
        modalState={workerInvitationModalState}
        openedFrom="GETTING_STARTED"
        onInvitationSuccess={() => {
          invalidateOnboardingSteps();
        }}
      />
    </Wrapper>
  );
}
